import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BaseWidgetProps } from '../../types/dashboard';
import { LIST_INQUIRIES } from '../../graphql/inquiries';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface InquiryItem {
  id: string;
  firstName: string;
  lastName: string;
  status: string;
  leadScore?: number;
  incidentType?: string;
  createdAt: string;
  assignedToName?: string;
}

interface ListInquiriesData {
  listInquiries: {
    items: InquiryItem[];
  };
}

export function InquirySummaryWidget({
  widget,
  isEditing: _isEditing,
  onConfigChange: _onConfigChange,
  onRemove: _onRemove,
  className = '',
}: BaseWidgetProps) {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<any[]>([]);

  const { data, loading, error, refetch } = useQuery<ListInquiriesData>(LIST_INQUIRIES, {
    variables: {
      limit: widget.config.maxItems || 10,
      status: widget.config.statusFilter || undefined,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: 30000, // Refresh every 30 seconds
  });

  useEffect(() => {
    if (data?.listInquiries?.items) {
      let sortedInquiries = [...data.listInquiries.items];
      
      // Sort based on configuration
      switch (widget.config.sortBy) {
        case 'leadScore':
          sortedInquiries.sort((a, b) => (b.leadScore || 0) - (a.leadScore || 0));
          break;
        case 'date':
          sortedInquiries.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'status':
          sortedInquiries.sort((a, b) => a.status.localeCompare(b.status));
          break;
        default:
          sortedInquiries.sort((a, b) => (b.leadScore || 0) - (a.leadScore || 0));
      }
      
      setInquiries(sortedInquiries);
    }
  }, [data, widget.config.sortBy]);

  const getLeadScoreColor = (score: number) => {
    if (score >= 80) return 'bg-red-100 text-red-700 border-red-200';
    if (score >= 60) return 'bg-orange-100 text-orange-700 border-orange-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'NEW': return 'bg-blue-100 text-blue-700';
      case 'REVIEWING': return 'bg-yellow-100 text-yellow-700';
      case 'ACCEPTED': return 'bg-green-100 text-green-700';
      case 'REJECTED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            Inquiry Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            Inquiry Pipeline
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-gray-600">Failed to load inquiries</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4" size="sm">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalInquiries = inquiries.length;
  const highScoreCount = inquiries.filter(i => i.leadScore >= 80).length;
  const newCount = inquiries.filter(i => i.status === 'NEW').length;

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            Inquiry Pipeline
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/inquiries')}
            className="text-aalaw-red hover:text-aalaw-red hover:bg-red-50"
          >
            View All
            <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-aalaw-charcoal">{totalInquiries}</div>
            <div className="text-xs text-gray-600 mt-1">Total</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-red-600">{highScoreCount}</div>
            <div className="text-xs text-gray-600 mt-1">High Priority</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{newCount}</div>
            <div className="text-xs text-gray-600 mt-1">New</div>
          </div>
        </div>

        {/* Inquiry List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {inquiries.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No inquiries found</h3>
              <p className="text-xs text-gray-600">New inquiries will appear here</p>
            </div>
          ) : (
            inquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                className="border border-gray-200 rounded-lg p-3 hover:shadow-md hover:border-aalaw-red transition-all cursor-pointer"
                onClick={() => navigate(`/inquiries/${inquiry.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        inquiry.leadScore >= 80 ? 'bg-red-600' : 
                        inquiry.leadScore >= 60 ? 'bg-orange-500' : 
                        'bg-gray-400'
                      }`}></div>
                      <h4 className="font-semibold text-aalaw-charcoal text-sm truncate">
                        {inquiry.firstName} {inquiry.lastName}
                      </h4>
                    </div>
                    <p className="text-xs text-gray-600 truncate">
                      {inquiry.incidentType}
                    </p>
                  </div>
                  
                  {widget.config.showLeadScores && inquiry.leadScore && (
                    <Badge className={`text-xs border ml-2 flex-shrink-0 ${getLeadScoreColor(inquiry.leadScore)}`}>
                      {inquiry.leadScore}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs ${getStatusColor(inquiry.status)}`}>
                      {inquiry.status}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {format(new Date(inquiry.createdAt), 'MMM d')}
                  </div>
                </div>
                
                {inquiry.assignedToName && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-gray-600">
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {inquiry.assignedToName}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>Updated {format(new Date(), 'h:mm a')}</span>
            <Button onClick={() => refetch()} variant="ghost" size="sm" className="h-7 text-xs">
              <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}